// src/router.tsx — React Router v7 route configuration
//
// Replaces Next.js file-based routing with explicit route definitions.
// Layout components (DashboardLayout, DocsLayout) wrap their children via <Outlet />.
// Public pages are standalone and bring their own Fieldbook chrome (FieldHeader/Footer).

import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Spinner } from "@/components/ui";

// --- Layouts ---
import DashboardLayout from "@/layouts/DashboardLayout";
import DocsLayout from "@/layouts/DocsLayout";

// --- Lazy-loaded pages ---
// Public pages
const HomePage = lazy(() => import("@/pages/HomePage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const BlogListPage = lazy(() => import("@/pages/BlogListPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
const ConnectivityPage = lazy(() => import("@/pages/ConnectivityPage"));
const ClaimsCalculatorPage = lazy(() => import("@/pages/ClaimsCalculatorPage"));
const RuralHealthPage = lazy(() => import("@/pages/RuralHealthPage"));
const RuralHealthReadinessPage = lazy(() => import("@/pages/RuralHealthReadinessPage"));
const ShlQuickstartPage = lazy(() => import("@/pages/ShlQuickstartPage"));
const ShlViewerPage = lazy(() => import("@/pages/ShlViewerPage"));
const InviteAcceptPage = lazy(() => import("@/pages/InviteAcceptPage"));
const SubscribeSuccessPage = lazy(() => import("@/pages/SubscribeSuccessPage"));

// Dashboard pages
const DashboardHomePage = lazy(() => import("@/pages/dashboard/DashboardHomePage"));
const CredentialsPage = lazy(() => import("@/pages/dashboard/CredentialsPage"));
const ProfilePage = lazy(() => import("@/pages/dashboard/ProfilePage"));
const SettingsPage = lazy(() => import("@/pages/dashboard/SettingsPage"));
const BillingPage = lazy(() => import("@/pages/dashboard/BillingPage"));
const TeamPage = lazy(() => import("@/pages/dashboard/TeamPage"));
const OrganizationsPage = lazy(() => import("@/pages/dashboard/OrganizationsPage"));
const NewOrganizationPage = lazy(() => import("@/pages/dashboard/NewOrganizationPage"));
const EditOrganizationPage = lazy(() => import("@/pages/dashboard/EditOrganizationPage"));
const AdminUsersPage = lazy(() => import("@/pages/dashboard/AdminUsersPage"));
const AdminEtlStatusPage = lazy(() => import("@/pages/dashboard/AdminEtlStatusPage"));

// Docs pages
const DocsPage = lazy(() => import("@/pages/DocsPage"));

// Design lab (isolated aesthetic previews — not part of production layouts)
const DesignLabIndex = lazy(() => import("@/pages/design-lab/DesignLabIndex"));
const LabKindred = lazy(() => import("@/pages/design-lab/Kindred"));
const LabHueRoom = lazy(() => import("@/pages/design-lab/HueRoom"));
const LabFieldbook = lazy(() => import("@/pages/design-lab/Fieldbook"));
const LabLithograph = lazy(() => import("@/pages/design-lab/Lithograph"));
const LabApiTool = lazy(() => import("@/pages/design-lab/ApiTool"));
const LabHomePreview = lazy(() => import("@/pages/design-lab/HomePreview"));
const LabDocsPreview = lazy(() => import("@/pages/design-lab/DocsPreview"));
const LabDashboardPreview = lazy(() => import("@/pages/design-lab/DashboardPreview"));
const LabBlogPreview = lazy(() => import("@/pages/design-lab/BlogPreview"));
const LabBlogPostPreview = lazy(() => import("@/pages/design-lab/BlogPostPreview"));

// Tools (new Fieldbook-language pages)
const ToolsHubPage = lazy(() => import("@/pages/tools/ToolsHubPage"));
const NpiLookupPage = lazy(() => import("@/pages/tools/NpiLookupPage"));
const ApiPlaygroundPage = lazy(() => import("@/pages/tools/ApiPlaygroundPage"));

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex-1 flex justify-center items-center py-20">
          <Spinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export function AppRouter() {
  return (
    <Routes>
      {/* New Fieldbook-language pages — self-contained chrome (standalone) */}
      <Route path="/" element={<PageSuspense><HomePage /></PageSuspense>} />
      <Route path="blog" element={<PageSuspense><BlogListPage /></PageSuspense>} />
      <Route path="blog/:slug" element={<PageSuspense><BlogPostPage /></PageSuspense>} />
      <Route path="pricing" element={<PageSuspense><PricingPage /></PageSuspense>} />
      <Route path="connectivity" element={<PageSuspense><ConnectivityPage /></PageSuspense>} />
      <Route path="claims-calculator" element={<PageSuspense><ClaimsCalculatorPage /></PageSuspense>} />
      <Route path="rural-health" element={<PageSuspense><RuralHealthPage /></PageSuspense>} />
      <Route path="rural-health/readiness" element={<PageSuspense><RuralHealthReadinessPage /></PageSuspense>} />
      <Route path="status" element={<PageSuspense><StatusPage /></PageSuspense>} />
      <Route path="shl/quickstart" element={<PageSuspense><ShlQuickstartPage /></PageSuspense>} />
      <Route path="shl/viewer" element={<PageSuspense><ShlViewerPage /></PageSuspense>} />
      <Route path="invite/accept" element={<PageSuspense><InviteAcceptPage /></PageSuspense>} />
      <Route path="subscribe-success" element={<PageSuspense><SubscribeSuccessPage /></PageSuspense>} />

      {/* Dashboard routes with auth guard + sidebar */}
      <Route path="dashboard" element={<DashboardLayout />}>
        <Route index element={<PageSuspense><DashboardHomePage /></PageSuspense>} />
        <Route path="credentials" element={<PageSuspense><CredentialsPage /></PageSuspense>} />
        <Route path="profile" element={<PageSuspense><ProfilePage /></PageSuspense>} />
        <Route path="settings" element={<PageSuspense><SettingsPage /></PageSuspense>} />
        <Route path="billing" element={<PageSuspense><BillingPage /></PageSuspense>} />
        <Route path="team" element={<PageSuspense><TeamPage /></PageSuspense>} />
        <Route path="organizations" element={<PageSuspense><OrganizationsPage /></PageSuspense>} />
        <Route path="organizations/new" element={<PageSuspense><NewOrganizationPage /></PageSuspense>} />
        <Route path="organizations/:orgId/edit" element={<PageSuspense><EditOrganizationPage /></PageSuspense>} />
        <Route path="admin/users" element={<PageSuspense><AdminUsersPage /></PageSuspense>} />
        <Route path="admin/etl-status" element={<PageSuspense><AdminEtlStatusPage /></PageSuspense>} />
      </Route>

      {/* Docs routes with sidebar */}
      <Route path="docs" element={<DocsLayout />}>
        <Route index element={<PageSuspense><DocsPage /></PageSuspense>} />
        <Route path="*" element={<PageSuspense><DocsPage /></PageSuspense>} />
      </Route>

      {/* Design lab — standalone aesthetic previews */}
      <Route path="design-lab" element={<PageSuspense><DesignLabIndex /></PageSuspense>} />
      <Route path="design-lab/kindred" element={<PageSuspense><LabKindred /></PageSuspense>} />
      <Route path="design-lab/hueroom" element={<PageSuspense><LabHueRoom /></PageSuspense>} />
      <Route path="design-lab/fieldbook" element={<PageSuspense><LabFieldbook /></PageSuspense>} />
      <Route path="design-lab/lithograph" element={<PageSuspense><LabLithograph /></PageSuspense>} />
      <Route path="design-lab/api-tool" element={<PageSuspense><LabApiTool /></PageSuspense>} />
      <Route path="design-lab/home" element={<PageSuspense><LabHomePreview /></PageSuspense>} />
      <Route path="design-lab/docs" element={<PageSuspense><LabDocsPreview /></PageSuspense>} />
      <Route path="design-lab/app" element={<PageSuspense><LabDashboardPreview /></PageSuspense>} />
      <Route path="design-lab/blog" element={<PageSuspense><LabBlogPreview /></PageSuspense>} />
      <Route path="design-lab/blog-post" element={<PageSuspense><LabBlogPostPreview /></PageSuspense>} />

      {/* Tools — new Fieldbook-language pages (own chrome until header/footer unify) */}
      <Route path="tools" element={<PageSuspense><ToolsHubPage /></PageSuspense>} />
      <Route path="tools/npi-lookup" element={<PageSuspense><NpiLookupPage /></PageSuspense>} />
      <Route path="tools/api-playground" element={<PageSuspense><ApiPlaygroundPage /></PageSuspense>} />

      {/* Legacy redirects */}
      <Route path="login" element={<Navigate to="/dashboard" replace />} />
      <Route path="login/*" element={<Navigate to="/dashboard" replace />} />

      {/* 404 fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
